import { Routes, Route, Navigate, useParams, useSearchParams } from 'react-router-dom'
import { useState, useEffect, lazy, Suspense } from 'react'
import { ThemeProvider } from '@/contexts/ThemeContext'
import { ToastProvider } from '@/contexts/ToastContext'
import { AuthContext } from '@/contexts/AuthContext'
import { ApiProvider } from '@/contexts/ApiContext'
import AppLayout from '@/components/layout/AppLayout'
import { api } from '@/lib/api'
import { isEnabled } from '@/lib/features'

// Lazy-loaded pages
const Landing = lazy(() => import('@/pages/Landing'))
const SignIn = lazy(() => import('@/pages/SignIn'))
const SignUp = lazy(() => import('@/pages/SignUp'))
const AcceptInvite = lazy(() => import('@/pages/AcceptInvite'))
const PlanSelection = lazy(() => import('@/pages/PlanSelection'))
const Demo = lazy(() => import('@/pages/Demo'))
const Privacy = lazy(() => import('@/pages/Privacy'))
const Terms = lazy(() => import('@/pages/Terms'))
const GetStarted = lazy(() => import('@/pages/GetStarted'))

const Home = lazy(() => import('@/pages/Home'))
// /lead-gen is now a tab on /find — see redirect route below.
// /buyer-gen and /agent-gen are now tabs on /buyers and /agents — these
// pages were removed in favor of in-page tabs. Routes below redirect for
// backwards compatibility.
const ParcelGen = lazy(() => import('@/pages/ParcelGen'))
const Research = lazy(() => import('@/pages/Research'))
const Find = lazy(() => import('@/pages/Find'))
const FindListDetail = lazy(() => import('@/pages/FindListDetail'))
const LeadsIntersect = lazy(() => import('@/pages/LeadsIntersect'))
const LeadDetail = lazy(() => import('@/pages/LeadDetail'))
const Inbound = lazy(() => import('@/pages/Inbound'))
const Eval = lazy(() => import('@/pages/Eval'))
const Deals = lazy(() => import('@/pages/Deals'))
const Schedule = lazy(() => import('@/pages/Schedule'))
const Approvals = lazy(() => import('@/pages/Approvals'))
const Listings = lazy(() => import('@/pages/Listings'))
const ListingDetail = lazy(() => import('@/pages/ListingDetail'))
const Contracts = lazy(() => import('@/pages/Contracts'))
const ContactDetail = lazy(() => import('@/pages/ContactDetail'))
const Buyers = lazy(() => import('@/pages/Buyers'))
const Agents = lazy(() => import('@/pages/Agents'))
const Wholesalers = lazy(() => import('@/pages/Wholesalers'))
const Outreach = lazy(() => import('@/pages/Outreach'))
const CampaignDetail = lazy(() => import('@/pages/CampaignDetail'))
const Contacts = lazy(() => import('@/pages/Contacts'))
const ChatRedirect = lazy(() => import('@/pages/ChatRedirect'))
const AgentHome = lazy(() => import('@/pages/AgentHome'))
const OpsRoom = lazy(() => import('@/pages/OpsRoom'))
const Operations = lazy(() => import('@/pages/Operations'))
const Integrations = lazy(() => import('@/pages/Integrations'))
const DataSources = lazy(() => import('@/pages/DataSources'))
const Settings = lazy(() => import('@/pages/Settings'))
const AdminSettings = lazy(() => import('@/pages/AdminSettings'))
const AgentSettings = lazy(() => import('@/pages/AgentSettings'))
const Guardrails = lazy(() => import('@/pages/Guardrails'))
const ToolStore = lazy(() => import('@/pages/ToolStore'))
const Knowledge = lazy(() => import('@/pages/Knowledge'))
const Jobs = lazy(() => import('@/pages/Jobs'))
const Admin = lazy(() => import('@/pages/Admin'))
const ParcelData = lazy(() => import('@/pages/ParcelData'))
const Parcels = lazy(() => import('@/pages/Parcels'))
const NotFound = lazy(() => import('@/pages/NotFound'))

// AI Systems
const Plans = lazy(() => import('@/pages/Plans'))
const Swarms = lazy(() => import('@/pages/Swarms'))
const Skills = lazy(() => import('@/pages/Skills'))
const CostDashboard = lazy(() => import('@/pages/CostDashboard'))
const MemoryBrowser = lazy(() => import('@/pages/MemoryBrowser'))
const Sessions = lazy(() => import('@/pages/Sessions'))

/** Preserves the :dealId param when redirecting /deals/:id → /opportunities/:id. */
function DealsLegacyRedirect() {
  const { dealId } = useParams<{ dealId: string }>()
  return <Navigate to={dealId ? `/opportunities/${dealId}` : '/opportunities'} replace />
}

/**
 * Authed user hits /signup or /signin. If they have an `?invite=...`
 * we route them to /accept-invite (which calls the protected accept
 * endpoint); otherwise just send them home.
 */
function AuthedAuthPageRedirect() {
  const [params] = useSearchParams()
  const invite = params.get('invite')
  if (invite) return <Navigate to={`/accept-invite?token=${encodeURIComponent(invite)}`} replace />
  return <Navigate to="/home" replace />
}

function PageLoader() {
  // Fixed viewport-fill so the spinner is centered regardless of where
  // it lives in the tree. The previous `h-full` would collapse to 0
  // when no ancestor explicitly sized it, parking the loader at the top
  // of the page.
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      style={{ background: 'var(--canvas)' }}
    >
      <div className="flex flex-col items-center gap-3">
        <div
          className="w-8 h-8 rounded-full animate-spin"
          style={{
            border: '2px solid var(--brand-500)',
            borderTopColor: 'transparent',
          }}
        />
        <span className="text-sm" style={{ color: 'var(--text-muted)' }}>Loading...</span>
      </div>
    </div>
  )
}

export default function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    return localStorage.getItem('forge-auth') === 'true'
  })
  const [hasOnboarded, setHasOnboarded] = useState<boolean | null>(null) // null = loading
  const [justOnboarded] = useState(false)

  // Validate session + check the post-signup onboarding-call gate. The
  // gate's source of truth is `organizations.activatedAt` (set by the
  // operator after the call), surfaced via /api/onboarding/status. We
  // also poll-check on every login so users who get activated mid-
  // session unlock on next page load.
  useEffect(() => {
    if (!isAuthenticated) return
    const token = api.getToken()
    if (!token) {
      // No JWT token stored — user signed in via mock path, force re-auth
      localStorage.removeItem('forge-auth')
      setIsAuthenticated(false)
      return
    }
    api.getOnboardingStatus()
      .then((status) => setHasOnboarded(!!status.activated))
      .catch(() => {
        // Endpoint unavailable (older API build, network blip): fall
        // back to the legacy /me check so the app stays usable. If
        // /me also fails the token's stale and we sign out.
        api.getMe().then((res: any) => {
          setHasOnboarded(res?.user?.onboardingCompleted !== false)
        }).catch(() => setHasOnboarded(true))
      })
  }, [isAuthenticated])

  if (!isAuthenticated) {
    return (
      <ThemeProvider>
        <ToastProvider>
          <Suspense fallback={<PageLoader />}>
            <Routes>
              <Route path="/" element={<Landing />} />
              <Route path="/signin" element={<SignIn onSignIn={() => { setIsAuthenticated(true) }} />} />
              <Route path="/login" element={<SignIn onSignIn={() => { setIsAuthenticated(true) }} />} />
              <Route path="/reset-password" element={<SignIn onSignIn={() => { setIsAuthenticated(true) }} />} />
              <Route path="/signup" element={<SignUp onSignUp={({ viaInvite }) => {
                setIsAuthenticated(true)
                // Invite signups bypass the onboarding-call gate (their
                // org admin already onboarded). Brand-new signups stay
                // gated until they book + we activate them.
                setHasOnboarded(viaInvite ? true : false)
              }} />} />
              <Route path="/accept-invite" element={<AcceptInvite isAuthenticated={false} />} />
              <Route path="/plans" element={<PlanSelection />} />
              <Route path="/demo" element={<Demo />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Suspense>
        </ToastProvider>
      </ThemeProvider>
    )
  }

  if (hasOnboarded === null) {
    return (
      <ThemeProvider>
        <ToastProvider>
          <PageLoader />
        </ToastProvider>
      </ThemeProvider>
    )
  }

  if (!hasOnboarded) {
    // Post-signup gate. Every non-invite signup must book an onboarding
    // call before the rest of the app unlocks; the operator flips the
    // server's activation flag after the call + plan selection.
    // Sign-out is wired in so users who hit the gate aren't trapped —
    // they can leave, come back, and pick up the request mid-flight.
    return (
      <ThemeProvider>
        <ToastProvider>
          <Suspense fallback={<PageLoader />}>
            <GetStarted
              onSignOut={() => {
                localStorage.removeItem('forge-auth')
                api.clearToken()
                setIsAuthenticated(false)
                setHasOnboarded(null)
              }}
            />
          </Suspense>
        </ToastProvider>
      </ThemeProvider>
    )
  }

  return (
    <ThemeProvider>
      <ToastProvider>
        <ApiProvider>
          <AuthContext.Provider value={{ signOut: () => { localStorage.removeItem('forge-auth'); api.clearToken(); setIsAuthenticated(false) } }}>
            <Suspense fallback={<PageLoader />}>
              <Routes>
                <Route element={<AppLayout />}>
                  <Route path="/" element={<Navigate to={justOnboarded ? "/eval" : "/home"} replace />} />
                  <Route path="/signin" element={<AuthedAuthPageRedirect />} />
                  <Route path="/signup" element={<AuthedAuthPageRedirect />} />
                  <Route path="/accept-invite" element={<AcceptInvite isAuthenticated={true} />} />
                  <Route path="/plans" element={<Navigate to="/home" replace />} />
                  {/* Legal pages — same chrome whether authed or not */}
                  <Route path="/privacy" element={<Privacy />} />
                  <Route path="/terms" element={<Terms />} />
                  <Route path="/home" element={<Home />} />
                  <Route path="/lead-gen" element={<Navigate to="/find?tab=find" replace />} />
                  <Route path="/buyer-gen" element={<Navigate to="/buyers?tab=find" replace />} />
                  <Route path="/agent-gen" element={<Navigate to="/agents?tab=find" replace />} />
                  <Route path="/parcel-gen" element={<ParcelGen />} />
                  <Route path="/research" element={<Research />} />
                  <Route path="/find" element={<Find />} />
                  <Route path="/find/intersect" element={<LeadsIntersect />} />
                  <Route path="/find/:listId" element={<FindListDetail />} />
                  <Route path="/leads/:id" element={<LeadDetail />} />
                  <Route path="/inbound" element={<Inbound />} />
                  <Route path="/eval" element={<Eval />} />
                  <Route path="/eval/:address" element={<Eval />} />
                  <Route path="/opportunities" element={<Deals />} />
                  <Route path="/opportunities/:dealId" element={<ContactDetail />} />
                  {/* Legacy /deals URLs — redirect to canonical /opportunities */}
                  <Route path="/deals" element={<Navigate to="/opportunities" replace />} />
                  <Route path="/deals/:dealId" element={<DealsLegacyRedirect />} />
                  <Route path="/contracts" element={<Contracts />} />
                  <Route path="/buyers" element={<Buyers />} />
                  <Route path="/agents" element={<Agents />} />
                  <Route path="/agents/:role" element={<AgentHome />} />
                  <Route path="/ops" element={<OpsRoom />} />
                  <Route path="/wholesalers" element={<Wholesalers />} />
                  <Route path="/outreach" element={<Outreach />} />
                  <Route path="/schedule" element={<Schedule />} />
                  <Route path="/approvals" element={<Approvals />} />
                  <Route path="/listings" element={<Listings />} />
                  <Route path="/listings/:id" element={<ListingDetail />} />
                  <Route path="/outreach/campaigns/:campaignId" element={<CampaignDetail />} />
                  <Route path="/contacts" element={<Contacts />} />
                  <Route path="/chat" element={<ChatRedirect />} />
                  <Route path="/chat/:conversationId" element={<ChatRedirect />} />
                  {isEnabled('operations') && <Route path="/operations" element={<Operations />} />}
                  <Route path="/integrations" element={<Integrations />} />
                  <Route path="/data-sources" element={<DataSources />} />
                  <Route path="/settings" element={<Settings />} />
                  <Route path="/admin" element={<AdminSettings />} />
                  {isEnabled('agentSettings') && <Route path="/settings/agents" element={<AgentSettings />} />}
                  {isEnabled('guardrails') && <Route path="/guardrails" element={<Guardrails />} />}
                  <Route path="/tool-store" element={<ToolStore />} />
                  <Route path="/knowledge" element={<Knowledge />} />
                  <Route path="/jobs" element={<Jobs />} />
                  {isEnabled('admin') && <Route path="/admin" element={<Admin />} />}
                  <Route path="/parcel-data" element={<ParcelData />} />
                  <Route path="/parcels" element={<Parcels />} />
                  {isEnabled('plans') && <Route path="/plans" element={<Plans />} />}
                  {isEnabled('swarms') && <Route path="/swarms" element={<Swarms />} />}
                  {isEnabled('skills') && <Route path="/skills" element={<Skills />} />}
                  {isEnabled('costs') && <Route path="/costs" element={<CostDashboard />} />}
                  {isEnabled('memory') && <Route path="/memory" element={<MemoryBrowser />} />}
                  {isEnabled('sessions') && <Route path="/sessions" element={<Sessions />} />}
                  <Route path="*" element={<NotFound />} />
                </Route>
              </Routes>
            </Suspense>
          </AuthContext.Provider>
        </ApiProvider>
      </ToastProvider>
    </ThemeProvider>
  )
}
